import React, { useState } from 'react';
import { uploadEvidence } from '../../services/factFinderApi';

const EVIDENCE_TYPES = [
  { value: 'photo', label: 'Photo' },
  { value: 'screenshot', label: 'Screenshot' },
  { value: 'email', label: 'Email' },
  { value: 'text_message', label: 'Text Message' },
  { value: 'document', label: 'Document' },
];

function EvidenceUpload({ evidence, setEvidence }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const result = await uploadEvidence(file, 'document', '');
      if (result.success) {
        setEvidence([...evidence, {
          type: 'document',
          filename: result.filename,
          url: result.url,
          description: '',
          uploaded_at: result.uploaded_at,
        }]);
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  function updateEvidence(index, field, value) {
    const updated = evidence.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setEvidence(updated);
  }

  function removeEvidence(index) {
    setEvidence(evidence.filter((_, i) => i !== index));
  }

  return (
    <div className="ff-card" data-testid="evidence-upload">
      <div className="ff-section-header">
        <div>
          <h2>Evidence</h2>
          <p className="ff-card-description">
            Attach photos, screenshots, emails, or documents related to this case.
          </p>
        </div>
        <label className={`ff-btn-outline ff-add-btn ${uploading ? 'ff-uploading' : ''}`}>
          {uploading ? 'Uploading...' : '+ Upload File'}
          <input
            type="file"
            className="ff-hidden-input"
            onChange={handleFileSelect}
            disabled={uploading}
            accept="image/*,.pdf,.doc,.docx,.txt,.msg,.eml"
            data-testid="evidence-file-input"
          />
        </label>
      </div>

      {error && <div className="ff-inline-error">{error}</div>}

      {evidence.length === 0 && (
        <p className="ff-empty-state">No evidence uploaded yet.</p>
      )}

      <div className="ff-evidence-list">
        {evidence.map((item, index) => (
          <div key={index} className="ff-evidence-item" data-testid={`evidence-${index}`}>
            <div className="ff-evidence-preview">
              {item.url && (item.type === 'photo' || item.type === 'screenshot') ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                  alt={item.filename}
                  className="ff-evidence-thumb"
                />
              ) : (
                <div className="ff-evidence-icon">
                  {item.type === 'email' ? '✉' : item.type === 'text_message' ? '💬' : '📄'}
                </div>
              )}
            </div>
            <div className="ff-evidence-details">
              <div className="ff-evidence-filename">{item.filename}</div>
              <div className="ff-evidence-meta">
                <select
                  className="ff-select ff-select-sm"
                  value={item.type}
                  onChange={(e) => updateEvidence(index, 'type', e.target.value)}
                  data-testid={`evidence-type-${index}`}
                >
                  {EVIDENCE_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="ff-input ff-input-sm"
                  value={item.description}
                  onChange={(e) => updateEvidence(index, 'description', e.target.value)}
                  placeholder="Brief description..."
                  data-testid={`evidence-desc-${index}`}
                />
              </div>
            </div>
            <button
              type="button"
              className="ff-remove-btn"
              onClick={() => removeEvidence(index)}
              data-testid={`remove-evidence-${index}`}
              title="Remove evidence"
            >
              &times;
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default EvidenceUpload;
